import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Settings as SettingsIcon, FileBadge, Boxes, Clock, Plus, Trash2, Tag, Palette, Upload, X, DatabaseBackup, FileJson, FileSpreadsheet, Hash, FlaskConical, Mail, Send, BellRing, PackagePlus } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { uploadFile, downloadTenantBackup, sendWeeklyDigestTest, getMe, updateAlertPreferences, sendDailyAuditSummaryTest } from "@/lib/api";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const WORKDAY_KEYS = ["dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat", "daySun"];

// Inline-editable packaging label (reverts on rejected rename).
function PackagingLabelInput({ p, onRename, title, testId }) {
  const [val, setVal] = useState(p.label);
  const commit = async () => {
    const ok = await onRename(p, val);
    if (!ok) setVal(p.label);
  };
  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      className="h-8 w-36 font-medium"
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={testId}
      title={title}
    />
  );
}

export const CompanyInfoCard = ({ f, set }) => {
  const { t } = useT();
  return (
    <Card className="lg:col-span-7">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <SettingsIcon className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.company")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label>{t("settings.legalName")}</Label>
          <Input value={f.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} data-testid="company-legalname-input" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("common.address")}</Label>
          <Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="company-address-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("clients.taxId")}</Label>
            <Input value={f.tax_id || ""} onChange={(e) => set("tax_id", e.target.value)} data-testid="company-taxid-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("clients.regNo")}</Label>
            <Input value={f.reg_no || ""} onChange={(e) => set("reg_no", e.target.value)} data-testid="company-regno-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.phone")}</Label>
            <Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="company-phone-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.email")}</Label>
            <Input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="company-email-input" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const CocCard = ({ f, set }) => {
  const { t } = useT();
  return (
    <Card className="lg:col-span-5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileBadge className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.coc")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label>{t("settings.docTitle")}</Label>
          <Input value={f.coc_title || ""} onChange={(e) => set("coc_title", e.target.value)} data-testid="company-coctitle-input" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("settings.declarationText")}</Label>
          <Textarea rows={6} value={f.declaration_text || ""} onChange={(e) => set("declaration_text", e.target.value)} data-testid="company-declaration-input" />
          <p className="text-xs text-muted-foreground">{t("settings.declarationHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("settings.footerText")}</Label>
          <Input value={f.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} data-testid="company-footer-input" />
        </div>
      </CardContent>
    </Card>
  );
};

export const PackagingCard = ({ packagings, packV, newPack, setNewPack, newNet, setNewNet, addPack, removePack, toggleActive, saveNet, renamePack }) => {
  const { t } = useT();
  return (
    <Card className="lg:col-span-7">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Boxes className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.packagingOptions")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.packagingHint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          {packagings.map((p) => (
            <div key={p.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${p.active !== false ? "border-border" : "border-dashed border-border opacity-70"}`} data-testid={`packaging-row-${p.id}`}>
              <PackagingLabelInput
                key={`pkg-label-${p.id}-${p.label}`}
                p={p}
                onRename={renamePack}
                title={t("settings.renameHint")}
                testId={`packaging-label-${p.id}`}
              />
              <button type="button" onClick={() => toggleActive(p)} title={p.active !== false ? t("settings.activeToggle") : t("settings.inactiveToggle")} data-testid={`packaging-toggle-${p.id}`}>
                {p.active !== false
                  ? <Badge className="badge-ok text-[10px]">{t("settings.active")}</Badge>
                  : <Badge className="badge-warn text-[10px]">{t("settings.inactive")}</Badge>}
              </button>
              <div className="ml-auto flex items-center gap-1.5">
                <Label className="text-[11px] text-muted-foreground">{t("settings.netShort")}</Label>
                <Input
                  key={`net-${p.id}-${packV}`}
                  type="number" step="0.001" min="0"
                  className="h-8 w-24 text-right font-mono"
                  defaultValue={p.net_kg ?? ""}
                  onBlur={(e) => saveNet(p, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  data-testid={`packaging-net-${p.id}`}
                />
                <span className="text-[11px] text-muted-foreground">kg</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removePack(p.id)} data-testid={`packaging-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-[hsl(var(--danger))]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t border-border pt-3">
          <div className="flex-1 space-y-1">
            <Label className="text-xs text-muted-foreground">{t("settings.newPackaging")}</Label>
            <Input placeholder={t("settings.packPlaceholder")} value={newPack} onChange={(e) => setNewPack(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPack(); } }} data-testid="packaging-new-input" />
          </div>
          <div className="w-28 space-y-1">
            <Label className="text-xs text-muted-foreground">{t("settings.net")}</Label>
            <Input type="number" step="0.001" min="0" placeholder="0.5" className="text-right font-mono" value={newNet} onChange={(e) => setNewNet(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPack(); } }} data-testid="packaging-new-net-input" />
          </div>
          <Button size="sm" variant="outline" onClick={addPack} data-testid="packaging-add-button"><Plus className="mr-1 h-4 w-4" /> {t("common.add")}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const ProductTypesCard = ({ allTypes, td, toggleTypeActive, removeType, newType, setNewType, addType }) => {
  const { t } = useT();
  return (
    <Card className="lg:col-span-5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.productTypes")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.productTypesHint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          {allTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="product-types-empty">{t("common.loading")}</p>
          ) : allTypes.map((pt) => (
            <div key={pt.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${pt.active !== false ? "border-border" : "border-dashed border-border opacity-70"}`} data-testid={`product-type-row-${pt.id}`}>
              <button type="button" className="flex items-center gap-2 text-left" onClick={() => toggleTypeActive(pt)} title={pt.active !== false ? t("settings.activeToggle") : t("settings.inactiveToggle")} data-testid={`product-type-toggle-${pt.id}`}>
                <span className="text-sm font-medium">{td(pt.name)}</span>
                {pt.active !== false
                  ? <Badge className="badge-ok text-[10px]">{t("settings.active")}</Badge>
                  : <Badge className="badge-warn text-[10px]">{t("settings.inactive")}</Badge>}
              </button>
              <div className="ml-auto">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeType(pt.id)} data-testid={`product-type-delete-${pt.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-[hsl(var(--danger))]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t border-border pt-3">
          <div className="flex-1 space-y-1">
            <Label className="text-xs text-muted-foreground">{t("settings.newProductType")}</Label>
            <Input placeholder={t("settings.productTypePlaceholder")} value={newType} onChange={(e) => setNewType(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addType(); } }} data-testid="product-type-new-input" />
          </div>
          <Button size="sm" variant="outline" onClick={addType} data-testid="product-type-add-button"><Plus className="mr-1 h-4 w-4" /> {t("common.add")}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const PackedOnOrderCard = ({ f, set, productTypes, td, togglePacked }) => {
  const { t } = useT();
  return (
    <Card className="lg:col-span-5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.packedTitle")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.packedHint")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {productTypes.map((p) => {
            const on = (f.packed_on_order_products || []).includes(p);
            return (
              <button key={p} type="button" onClick={() => togglePacked(p)} data-testid={`packed-toggle-${p}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-[hsl(var(--brand))] text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
                {td(p)}
              </button>
            );
          })}
        </div>
        <div className="space-y-1.5">
          <Label>{t("settings.minMatLabel")}</Label>
          <Input type="number" className="w-32 text-right font-mono" value={f.min_maturation_days_for_sale ?? 5}
            onChange={(e) => set("min_maturation_days_for_sale", e.target.value)} data-testid="min-maturation-input" />
          <p className="text-xs text-muted-foreground">{t("settings.minMatHint")}</p>
        </div>
        {(f.packed_on_order_products || []).length > 0 && (
          <Badge className="badge-warn">{t("settings.packedBadge", { products: (f.packed_on_order_products || []).map((p) => td(p)).join(", "), n: f.min_maturation_days_for_sale ?? 5 })}</Badge>
        )}
      </CardContent>
    </Card>
  );
};

const PRESET_COLORS = ["#0f766e", "#2563eb", "#7c3aed", "#db2777", "#ea580c", "#16a34a", "#0891b2", "#dc2626"];

export const BrandingCard = ({ f, set }) => {
  const { t } = useT();
  const [uploading, setUploading] = React.useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const logo = f.logo_url ? (f.logo_url.startsWith("http") ? f.logo_url : `${backendUrl}${f.logo_url}`) : null;
  const accent = f.accent_color || "#0f766e";

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      set("logo_url", res.url);
      toast.success(t("settings.logoUploaded"));
    } catch { toast.error(t("settings.failedSave")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <Card className="lg:col-span-12" data-testid="branding-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Palette className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.brandingSection")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.brandingHint")}</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label>{t("settings.brandName")}</Label>
          <Input value={f.brand_name || ""} placeholder="LactoPilot" onChange={(e) => set("brand_name", e.target.value)} data-testid="branding-name-input" />
          <p className="text-xs text-muted-foreground">{t("settings.brandNameHint")}</p>
        </div>

        <div className="space-y-1.5">
          <Label>{t("settings.logo")}</Label>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary">
              {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" data-testid="branding-logo-preview" /> : <Palette className="h-5 w-5 text-muted-foreground" />}
            </div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary" data-testid="branding-logo-upload">
              <Upload className="h-4 w-4" /> {uploading ? t("common.loading") : t("settings.uploadLogo")}
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
            </label>
            {f.logo_url ? (
              <Button variant="ghost" size="icon" onClick={() => set("logo_url", "")} data-testid="branding-logo-remove">
                <X className="h-4 w-4 text-[hsl(var(--danger))]" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("settings.accentColor")}</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={accent} onChange={(e) => set("accent_color", e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-0.5" data-testid="branding-color-input" />
            <Input value={f.accent_color || ""} placeholder="#0f766e" onChange={(e) => set("accent_color", e.target.value)} className="w-32 font-mono" data-testid="branding-color-hex" />
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {PRESET_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => set("accent_color", c)} title={c}
                className="h-6 w-6 rounded-full border border-border transition-transform hover:scale-110"
                style={{ backgroundColor: c }} data-testid={`branding-preset-${c}`} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const LotNumberingCard = ({ f, set }) => {
  const { t } = useT();
  const on = !!f.lot_include_year;
  return (
    <Card data-testid="lot-numbering-card" className="lg:col-span-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Hash className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.lotNumbering")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.lotNumberingHint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="lot-include-year-switch">{t("settings.lotIncludeYear")}</Label>
            <p className="text-xs text-muted-foreground">{t("settings.lotIncludeYearHint")}</p>
          </div>
          <Switch id="lot-include-year-switch" checked={on}
            onCheckedChange={(v) => set("lot_include_year", !!v)} data-testid="lot-include-year-switch" />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="lot-numbering-example">
          <span>{t("settings.lotFormatExample")}</span>
          <Badge variant="secondary" className="font-mono">{on ? "020726-003" : "0207-003"}</Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export const YieldCalcCard = ({ f, set }) => {
  const { t } = useT();
  return (
    <Card data-testid="yield-calc-card" className="lg:col-span-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FlaskConical className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.yieldCalc")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.yieldCalcHint")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("settings.milkDensity")}</Label>
          <Input type="number" step="0.001" min="0.5" className="w-full max-w-[10rem] text-right font-mono"
            value={f.milk_density_kg_per_l ?? 1.03}
            onChange={(e) => set("milk_density_kg_per_l", e.target.value)} data-testid="milk-density-input" />
          <p className="text-xs text-muted-foreground">{t("settings.milkDensityHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("settings.milkMaxTemp")}</Label>
          <Input type="number" step="0.5" min="0.5" max="40" className="w-full max-w-[10rem] text-right font-mono"
            value={f.milk_max_temp_c ?? 6}
            onChange={(e) => set("milk_max_temp_c", e.target.value)} data-testid="milk-max-temp-input" />
          <p className="text-xs text-muted-foreground">{t("settings.milkMaxTempHint")}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Pickable digest sections — keys must match backend DIGEST_SECTION_KEYS.
const DIGEST_SECTIONS = [
  { key: "production_summary", labelKey: "digestSecProduction" },
  { key: "over_target", labelKey: "digestSecOverTarget" },
  { key: "stock_movements", labelKey: "digestSecStock" },
  { key: "expiring", labelKey: "digestSecExpiring" },
  { key: "losses", labelKey: "digestSecLosses" },
  { key: "nonconformities", labelKey: "digestSecNcr" },
  { key: "audit_changes", labelKey: "digestSecAudit" },
];

export const WeeklyDigestCard = ({ f, set }) => {
  const { t } = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sending, setSending] = useState(false);
  const enabled = f.weekly_digest_enabled !== false; // ON by default
  const emailsText = f.weekly_digest_emails_text ?? (Array.isArray(f.weekly_digest_emails) ? f.weekly_digest_emails.join(", ") : "");
  const picks = f.weekly_digest_sections || {}; // missing key = section ON
  const isOn = (k) => picks[k] !== false;
  const toggleSection = (k) => {
    const next = Object.fromEntries(DIGEST_SECTIONS.map((s) => [s.key, isOn(s.key)]));
    next[k] = !isOn(k);
    set("weekly_digest_sections", next);
  };

  // Per-recipient section overrides: email -> {section: bool}. Missing = company picks.
  const parsedEmails = [...new Set(String(emailsText).split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@")))];
  const rs = f.weekly_digest_recipient_sections || {};
  const isCustom = (email) => !!rs[email];
  const recipOn = (email, k) => (rs[email] ? rs[email][k] !== false : isOn(k));
  const toggleCustom = (email) => {
    const next = { ...rs };
    if (next[email]) delete next[email];
    else next[email] = Object.fromEntries(DIGEST_SECTIONS.map((s) => [s.key, isOn(s.key)]));
    set("weekly_digest_recipient_sections", next);
  };
  const toggleRecipSection = (email, k) => {
    const cur = rs[email] || Object.fromEntries(DIGEST_SECTIONS.map((s) => [s.key, isOn(s.key)]));
    set("weekly_digest_recipient_sections", { ...rs, [email]: { ...cur, [k]: !recipOn(email, k) } });
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await sendWeeklyDigestTest();
      toast.success(t("settings.digestTestSent", { emails: (res.sent_to || []).join(", ") }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.digestTestFailed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="weekly-digest-card" className="lg:col-span-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.digestTitle")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.digestHint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="weekly-digest-switch">{t("settings.digestEnabled")}</Label>
            <p className="text-xs text-muted-foreground">{t("settings.digestEnabledHint")}</p>
          </div>
          <Switch id="weekly-digest-switch" checked={enabled}
            onCheckedChange={(v) => set("weekly_digest_enabled", !!v)} data-testid="weekly-digest-switch" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="weekly-digest-emails">{t("settings.digestEmails")}</Label>
          <Textarea id="weekly-digest-emails" rows={2} value={emailsText}
            onChange={(e) => set("weekly_digest_emails_text", e.target.value)}
            placeholder="manager@firma.ro, sef.productie@firma.ro" data-testid="weekly-digest-emails-input" />
          <p className="text-[11px] text-muted-foreground">{t("settings.digestEmailsHint")}</p>
        </div>
        <div className="space-y-2 rounded-lg border border-border p-3" data-testid="digest-sections-picker">
          <div className="space-y-0.5">
            <Label>{t("settings.digestSectionsTitle")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("settings.digestSectionsHint")}{!isAdmin && <> · {t("settings.digestSectionsAdminOnly")}</>}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {DIGEST_SECTIONS.map((s) => (
              <div key={s.key} className="flex items-center justify-between gap-3 rounded-md bg-secondary/40 px-2.5 py-1.5">
                <Label htmlFor={`digest-sec-${s.key}`} className="text-xs font-normal cursor-pointer">
                  {t(`settings.${s.labelKey}`)}
                </Label>
                <Switch id={`digest-sec-${s.key}`} checked={isOn(s.key)} disabled={!isAdmin}
                  onCheckedChange={() => toggleSection(s.key)} data-testid={`digest-section-${s.key}-switch`} />
              </div>
            ))}
          </div>
        </div>
        {parsedEmails.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border p-3" data-testid="digest-recipient-sections">
            <div className="space-y-0.5">
              <Label>{t("settings.digestPerRecipientTitle")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.digestPerRecipientHint")}{!isAdmin && <> · {t("settings.digestSectionsAdminOnly")}</>}
              </p>
            </div>
            <div className="space-y-2">
              {parsedEmails.map((email) => (
                <div key={email} className="rounded-md bg-secondary/40 p-2.5" data-testid={`digest-recipient-row-${email}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate font-mono text-xs">{email}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">
                        {isCustom(email) ? t("settings.digestRecipientCustom") : t("settings.digestRecipientDefault")}
                      </span>
                      <Switch checked={isCustom(email)} disabled={!isAdmin}
                        onCheckedChange={() => toggleCustom(email)}
                        data-testid={`digest-recipient-custom-${email}`} />
                    </div>
                  </div>
                  {isCustom(email) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {DIGEST_SECTIONS.map((s) => {
                        const on = recipOn(email, s.key);
                        return (
                          <button
                            key={s.key}
                            type="button"
                            disabled={!isAdmin}
                            onClick={() => toggleRecipSection(email, s.key)}
                            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${on
                              ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))] font-medium"
                              : "border-border bg-background text-muted-foreground line-through"}`}
                            data-testid={`digest-recipient-${email}-sec-${s.key}`}
                          >
                            {t(`settings.${s.labelKey}`)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={sendTest} disabled={sending}
          className="gap-1.5" data-testid="weekly-digest-test-button">
          <Send className="h-4 w-4" /> {sending ? t("settings.digestSending") : t("settings.digestTestBtn")}
        </Button>
      </CardContent>
    </Card>
  );
};

export const WorkScheduleCard = ({ f, set, toggleWorkingDay, toggleDeliveryDay }) => {
  const { t } = useT();
  return (
    <Card data-testid="work-schedule-card" className="lg:col-span-12">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.workSection")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("settings.workSectionHint")}</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("settings.standardWorkdayHours")}</Label>
          <Input type="number" step="0.5" min="0" className="w-full max-w-[12rem] text-right font-mono"
            value={f.standard_workday_hours ?? 8}
            onChange={(e) => set("standard_workday_hours", e.target.value)} data-testid="standard-workday-hours-input" />
          <p className="text-xs text-muted-foreground">{t("settings.standardWorkdayHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("settings.workingDays")}</Label>
          <div className="flex flex-wrap gap-2">
            {WORKDAY_KEYS.map((key, idx) => {
              const on = (Array.isArray(f.working_days) ? f.working_days : [0, 1, 2, 3, 4]).includes(idx);
              return (
                <button key={key} type="button" onClick={() => toggleWorkingDay(idx)} data-testid={`working-day-${idx}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-[hsl(var(--brand))] text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
                  {t(`settings.${key}`)}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">{t("settings.workingDaysHint")}</p>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>{t("settings.deliveryDays")}</Label>
          <div className="flex flex-wrap gap-2">
            {WORKDAY_KEYS.map((key, idx) => {
              const on = (Array.isArray(f.delivery_days) ? f.delivery_days : (Array.isArray(f.working_days) ? f.working_days : [0, 1, 2, 3, 4])).includes(idx);
              return (
                <button key={key} type="button" onClick={() => toggleDeliveryDay(idx)} data-testid={`delivery-day-${idx}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-[hsl(var(--brand))] text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
                  {t(`settings.${key}`)}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">{t("settings.deliveryDaysHint")}</p>
        </div>
      </CardContent>
    </Card>
  );
};


export const BackupCard = () => {
  const { t } = useT();
  const [busy, setBusy] = useState("");

  const run = async (fmt) => {
    setBusy(fmt);
    try {
      await downloadTenantBackup(fmt);
      toast.success(t("settings.backupStarted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.backupFailed"));
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="lg:col-span-12" data-testid="backup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <DatabaseBackup className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("settings.backupTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">{t("settings.backupHint")}</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => run("json")} disabled={!!busy} data-testid="backup-json-btn">
            <FileJson className="mr-1.5 h-4 w-4" />
            {busy === "json" ? t("settings.backupPreparing") : t("settings.backupJson")}
          </Button>
          <Button variant="outline" onClick={() => run("xlsx")} disabled={!!busy} data-testid="backup-xlsx-btn">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            {busy === "xlsx" ? t("settings.backupPreparing") : t("settings.backupExcel")}
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">{t("settings.backupNote")}</p>
      </CardContent>
    </Card>
  );
};

// Personal preference (admins only): choose how audit alert emails arrive —
// instant (each change), daily 18:00 summary, or off. Bell alerts always stay active.
export const AdminAlertsCard = () => {
  const { t } = useT();
  const { user } = useAuth();
  const [mode, setMode] = useState("instant");
  const [restock, setRestock] = useState({ email: true, bell: true });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    let alive = true;
    getMe()
      .then((me) => {
        if (!alive) return;
        setMode(me?.audit_email_mode || "instant");
        setRestock({ email: me?.alert_restock_email !== false, bell: me?.alert_restock_bell !== false });
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const toggleRestock = async (channel, value) => {
    const prev = restock;
    setRestock((r) => ({ ...r, [channel]: value }));
    try {
      await updateAlertPreferences({ [`alert_restock_${channel}`]: value });
      toast.success(t(value ? "settings.restockAlertOn" : "settings.restockAlertOff"));
    } catch {
      setRestock(prev);
      toast.error(t("settings.adminAlertsError"));
    }
  };

  if (user?.role !== "admin") return null;

  const MODES = [
    { value: "instant", label: t("settings.adminAlertsModeInstant"), hint: t("settings.adminAlertsModeInstantHint") },
    { value: "daily", label: t("settings.adminAlertsModeDaily"), hint: t("settings.adminAlertsModeDailyHint") },
    { value: "off", label: t("settings.adminAlertsModeOff"), hint: t("settings.adminAlertsModeOffHint") },
  ];

  const choose = async (next) => {
    if (next === mode || saving) return;
    setSaving(true);
    const prev = mode;
    setMode(next);
    try {
      await updateAlertPreferences({ audit_email_mode: next });
      toast.success(t(`settings.adminAlertsSaved_${next}`));
    } catch {
      setMode(prev);
      toast.error(t("settings.adminAlertsError"));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await sendDailyAuditSummaryTest();
      toast.success(t("settings.adminAlertsTestSent", { n: r?.events ?? 0 }));
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("settings.adminAlertsError"));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card data-testid="admin-alerts-card" className="lg:col-span-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BellRing className="h-4 w-4" /> {t("settings.adminAlertsTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-0.5">
          <Label>{t("settings.adminAlertsEmailLabel")}</Label>
          <p className="text-xs text-muted-foreground">{t("settings.adminAlertsHint")}</p>
        </div>
        <RadioGroup value={mode} onValueChange={choose} disabled={!loaded || saving} className="gap-2" data-testid="admin-alerts-mode-group">
          {MODES.map((m) => (
            <label
              key={m.value}
              htmlFor={`alert-mode-${m.value}`}
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-2.5 transition-colors ${mode === m.value ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.06)]" : "border-border bg-secondary/30 hover:bg-secondary/60"}`}
              data-testid={`admin-alerts-mode-${m.value}`}
            >
              <RadioGroupItem value={m.value} id={`alert-mode-${m.value}`} className="mt-0.5" />
              <span className="space-y-0.5">
                <span className="block text-sm font-medium">{m.label}</span>
                <span className="block text-xs text-muted-foreground">{m.hint}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
        <div className="rounded-md border border-dashed border-border p-3" data-testid="restock-alerts-block">
          <div className="flex items-start gap-2">
            <PackagePlus className="mt-0.5 h-4 w-4 text-[hsl(var(--brand))]" />
            <div className="space-y-0.5">
              <Label>{t("settings.restockAlertsTitle")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.restockAlertsHint")}</p>
            </div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <label className="flex items-center justify-between gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2" htmlFor="restock-email">
              <span className="flex items-center gap-2 text-sm"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> {t("settings.restockAlertEmail")}</span>
              <Switch id="restock-email" checked={restock.email} disabled={!loaded} onCheckedChange={(v) => toggleRestock("email", v)} data-testid="restock-alert-email-switch" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2" htmlFor="restock-bell">
              <span className="flex items-center gap-2 text-sm"><BellRing className="h-3.5 w-3.5 text-muted-foreground" /> {t("settings.restockAlertBell")}</span>
              <Switch id="restock-bell" checked={restock.bell} disabled={!loaded} onCheckedChange={(v) => toggleRestock("bell", v)} data-testid="restock-alert-bell-switch" />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">{t("settings.adminAlertsPersonal")}</p>
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sendingTest} data-testid="admin-alerts-test-button">
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {sendingTest ? t("settings.digestSending") : t("settings.adminAlertsTestBtn")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
